"use client";

import z from "zod";
import clsx from "clsx";
import React, {useEffect, useState} from "react";
import {Controller, FormProvider, SubmitErrorHandler, SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {SubmitButton} from "~/components/forms/SubmitButton";
import {TextAreaField, TextField} from "~/components/Forms";
import {Locale, zLang} from "@/i18n-config";
import {useReCaptcha, WithReCaptcha} from "@/components/providers/client/ReCaptchaProvider";
import {RecaptchaAction} from "@/utils/google/utils";
import {AnimationCompleted} from "@/components/animations/Completed";

export const dynamic = "force-dynamic";

const PointOfContactFormReCaptcha = (
    { lang, dictionary } : { lang: Locale, dictionary: any }
) => {

    const { containerId, loaded, isReCaptchaVisible, reCaptchaState, executeReCaptcha, renderReCaptcha, refreshReCaptcha } = useReCaptcha();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isCompleted, setIsCompleted] = useState<boolean>(false);

    const formSchema = z.object({
        lang: zLang("Invalid lang"),
        firstname: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorFirstnameRequired,
            })
            .trim()
            .min(2, dictionary.validationErrorLengthMin.replace("{min}", 2)),
        lastname: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorLastnameRequired,
            })
            .trim()
            .min(2, dictionary.validationErrorLengthMin.replace("{min}", 2)),
        email: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorEmailRequired,
            })
            .trim()
            .email({
                message: dictionary.validationErrorEmailRequired,
            }),
        phone: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorPhoneRequired,
            })
            .trim(),
        message: z
            .string({
                invalid_type_error: dictionary.validationErrorTypeString,
                required_error: dictionary.validationErrorMessageRequired,
            })
            .trim()
            .min(2, dictionary.validationErrorLengthMin.replace("{min}", 2)),
    });

    type FormSchema = z.infer<typeof formSchema>;

    const form = useForm<FormSchema>({
        resolver: zodResolver(formSchema),
        mode: "onBlur",
        values: {
            lang: lang,
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            message: "",
        }
    });

    const onSubmit: SubmitHandler<FormSchema> = async (data) => {

        setIsLoading(true);

        const response = await fetch(`/api/contact`, {
            method: "POST",
            body: JSON.stringify({
                ...reCaptchaState,
                ...data
            }),
            headers: {
                "Content-Type": "application/json"
            }
        });

        const result = await response.json();

        if(result.error) {
            setIsLoading(false);
            if(result.errorCaptchaValidation) {
                await renderReCaptcha(containerId);
            } else {
                await refreshReCaptcha(RecaptchaAction.CONTACT);
            }
            form.setError("root", {
                type: "manual",
                message: result.errorMessage,
            });
        } else {
            setIsCompleted(true);
        }

    };

    const onError: SubmitErrorHandler<FormSchema> = (
        errors,
        e
    ) => {
        console.error(errors);
    };

    useEffect(() => {
        if (!loaded) return;
        const runReCaptcha = async () => {
            await executeReCaptcha(RecaptchaAction.CONTACT);
        };
        runReCaptcha();
    }, [loaded, executeReCaptcha]);

    if(isCompleted) {
        return (
            <div className="flex flex-col items-center justify-center py-5 px-20 bg-white rounded-md">
                <div className="h-[150px] w-[300px]">
                    <AnimationCompleted/>
                </div>
            </div>
        );
    }

    return (
        <FormProvider {...form}>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-y-2 gap-x-4 mt-2">

                <input type="hidden" value={lang} {...form.register("lang")} />

                <Controller
                    control={form.control}
                    name="firstname"
                    rules={{required: true}}
                    render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                        return (
                            <TextField
                                label={dictionary.formFirstnameLabel}
                                placeholder={dictionary.formFirstnamePlaceholder}
                                name="firstname"
                                type="text"
                                onBlur={onBlur}
                                onChange={onChange}
                                defaultValue={value}
                                autoComplete="off"
                                className="sm:col-span-3"
                                error={error?.message}
                                required/>
                        );
                    }}
                />

                <Controller
                    control={form.control}
                    name="lastname"
                    rules={{required: true}}
                    render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                        return (
                            <TextField
                                label={dictionary.formLastnameLabel}
                                placeholder={dictionary.formLastnamePlaceholder}
                                name="lastname"
                                type="text"
                                onBlur={onBlur}
                                onChange={onChange}
                                defaultValue={value}
                                autoComplete="off"
                                className="sm:col-span-3"
                                error={error?.message}
                                required/>
                        );
                    }}
                />

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-y-2 gap-x-4 mt-2">

                <Controller
                    control={form.control}
                    name="email"
                    rules={{ required: true }}
                    render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                        return (
                            <TextField
                                label={dictionary.formEmailLabel}
                                placeholder={dictionary.formEmailPlaceholder}
                                name="email"
                                type="text"
                                onBlur={onBlur}
                                onChange={onChange}
                                defaultValue={value}
                                autoComplete="email"
                                className="sm:col-span-3"
                                error={error?.message}
                                required />
                        );
                    }}
                />

                <Controller
                    control={form.control}
                    name="phone"
                    rules={{ required: true }}
                    render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                        return (
                            <TextField
                                label={dictionary.formPhoneLabel}
                                placeholder={dictionary.formPhonePlaceholder}
                                name="phone"
                                type="text"
                                onBlur={onBlur}
                                onChange={onChange}
                                defaultValue={value}
                                autoComplete="tel"
                                className="sm:col-span-3"
                                error={error?.message} />
                        );
                    }}
                />

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-6 gap-y-2 gap-x-4 mt-2">

                <Controller
                    control={form.control}
                    name="message"
                    rules={{required: true}}
                    render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                        return (
                            <TextAreaField
                                label={dictionary.formMessageLabel}
                                noteClassName="text-white opacity-80"
                                placeholder={dictionary.formMessagePlaceholder}
                                rows={4}
                                name="message"
                                onBlur={onBlur}
                                onChange={onChange}
                                defaultValue={value}
                                autoComplete="off"
                                className="col-span-6"
                                error={error?.message}
                                required/>
                        );
                    }}
                />

                <div className={clsx(isReCaptchaVisible ? "visible" : "hidden", "flex items-center justify-center sm:col-span-6")}>
                    <div id={containerId}></div>
                </div>

            </div>

            <div className="mt-4 md:mt-8 md:flex md:flex-row md:gap-4 pb-4">

                <SubmitButton
                    pending={isLoading}
                    disabled={!loaded || !reCaptchaState.captcha}
                    className="mt-4 md:mt-0 w-full"
                    onClickAction={form.handleSubmit(onSubmit, onError)}
                    label={dictionary.submitSendRequest}/>

            </div>

        </FormProvider>
    );
}

export const PointOfContactForm = WithReCaptcha(PointOfContactFormReCaptcha);
